import React, { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

// Tractor beam - glowing cone under UFO. Color comes from the equipped
// beam_effect cosmetic (or sensible default). The cosmetic visual carries
// `color` (outer cone) and `emissive` (inner brighter cone / ground glow).
function Beam({ position, active, radius = 3, height = 10, color, innerColor }) {
  const meshRef = useRef();
  const innerRef = useRef();
  const beamColor = color || "#7ed957";
  const beamInner = innerColor || "#c2ff5d";

  useFrame((state) => {
    if (!meshRef.current) return;
    const t = state.clock.elapsedTime;
    if (active) {
      const pulse = 0.65 + Math.sin(t * 8) * 0.1;
      meshRef.current.material.opacity = pulse;
      meshRef.current.material.emissiveIntensity = 2 + Math.sin(t * 8) * 0.5;
      meshRef.current.rotation.y = t * 0.8;
      if (innerRef.current) {
        innerRef.current.material.opacity = 0.4 + Math.sin(t * 10) * 0.1;
        innerRef.current.rotation.y = -t * 1.2;
      }
    } else {
      meshRef.current.material.opacity = 0;
      meshRef.current.material.emissiveIntensity = 0;
      if (innerRef.current) innerRef.current.material.opacity = 0;
    }
  });

  if (!position) return null;
  // Cone: tip at UFO bottom, opens downward to ground
  // We center the cone at y = position[1] - height/2
  const cy = position[1] - height / 2;
  return (
    <group position={[position[0], cy, position[2]]}>
      {/* Outer cone */}
      <mesh ref={meshRef} rotation={[Math.PI, 0, 0]}>
        <coneGeometry args={[radius, height, 24, 1, true]} />
        <meshStandardMaterial
          color={beamColor}
          emissive={beamColor}
          emissiveIntensity={2}
          transparent
          opacity={0.7}
          side={THREE.DoubleSide}
          depthWrite={false}
          toneMapped={false}
        />
      </mesh>
      {/* Inner brighter cone */}
      <mesh ref={innerRef} rotation={[Math.PI, 0, 0]}>
        <coneGeometry args={[radius * 0.55, height, 18, 1, true]} />
        <meshStandardMaterial
          color={beamInner}
          emissive={beamInner}
          emissiveIntensity={2.5}
          transparent
          opacity={0.4}
          side={THREE.DoubleSide}
          depthWrite={false}
          toneMapped={false}
        />
      </mesh>
      {/* Ground glow disc */}
      <mesh position={[0, -height / 2 + 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]} visible={active}>
        <circleGeometry args={[radius, 24]} />
        <meshStandardMaterial
          color={beamInner}
          emissive={beamInner}
          emissiveIntensity={1.8}
          transparent
          opacity={active ? 0.45 : 0}
          toneMapped={false}
          depthWrite={false}
        />
      </mesh>
    </group>
  );
}

export default React.memo(Beam);
